import { useEffect, useRef } from 'react'

import { platformMyResources } from '@krp/platform-ui'
import { aqsiSalesAttention } from '@krp/resource-aqsi-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { AQSI_MINE } from './aqsiMine'

/** Число в меню обновляется само, без F5 (RP-FB35 K7). */
const POLL_MS = 60_000

type Subscription = { refetch: () => unknown; unsubscribe: () => void }

/**
 * Сколько продаж AQSI требуют внимания — по всем кабинетам, где у вас есть `sales.read`. Отдельный счётчик (не «отложенные»): сумма
 * `aqsiSalesAttention` по кабинетам. Подписка на кабинеты — по одной на rid, снимается при уходе кабинета и при размонтировании.
 */
export function useAttentionCounter(): number {
  const dispatch = useAppDispatch()
  const sources = platformMyResources.useQuery(AQSI_MINE.salesRead)
  const ids = (sources.data ?? []).map((source) => source.id).sort().join(',')
  const subscriptions = useRef(new Map<string, Subscription>())

  useEffect(() => {
    const wanted = new Set(ids === '' ? [] : ids.split(','))
    const held = subscriptions.current
    for (const [id, subscription] of held) {
      if (!wanted.has(id)) {
        subscription.unsubscribe()
        held.delete(id)
      }
    }
    for (const id of wanted) {
      if (!held.has(id)) held.set(id, dispatch(aqsiSalesAttention.initiate({ resourceId: id })))
    }
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') for (const subscription of held.values()) void subscription.refetch()
    }, POLL_MS)
    return () => clearInterval(timer)
  }, [dispatch, ids])

  useEffect(
    () => () => {
      for (const subscription of subscriptions.current.values()) subscription.unsubscribe()
      subscriptions.current.clear()
    },
    [],
  )

  return useAppSelector((state: RootState) =>
    ids === '' ? 0 : ids.split(',').reduce((sum, id) => sum + (aqsiSalesAttention.select({ resourceId: id })(state).data?.length ?? 0), 0),
  )
}
